import { Globe, Share2, Sparkles, Image as ImageIcon, RotateCcw, Lock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { ProBadge } from "@/components/ui/ProBadge";
import { ImageUpload } from "@/components/ui/ImageUpload";
import type { SeoOverrides } from "@profolio/validators";
import { mapSubscriptionToProductTier } from "@profolio/validators/tiers";

interface SeoPreviewPanelProps {
  profile: {
    fullName?: string | null;
    title?: string | null;
    bio?: string | null;
    photoUrl?: string | null;
    subdomain?: string | null;
    colorPalette?: string | null;
    seoOverrides?: SeoOverrides | null;
  };
  userTier?: string;
  onUpgradeClick?: () => void;
  onUpdateSeo?: (seo: SeoOverrides) => void;
}

export function SeoPreviewPanel({
  profile,
  userTier = "starter",
  onUpgradeClick,
  onUpdateSeo,
}: SeoPreviewPanelProps) {
  const productTier = mapSubscriptionToProductTier(userTier);
  const isProUser = productTier === "pro" || productTier === "premium";

  const name = profile.fullName || "Your Name";
  const defaultBio = profile.bio || `Official academic profile and research portfolio for ${name}.`;
  const subdomain = profile.subdomain || "username";

  // Resolved values (Overridden OR Default)
  const autoTitle = `${name}${profile.title ? ` - ${profile.title}` : ""} | Profolio.lk`;
  const autoDescription = defaultBio.slice(0, 160);

  const customTitle = profile.seoOverrides?.ogTitle || "";
  const customDescription = profile.seoOverrides?.ogDescription || "";
  const customImageUrl = profile.seoOverrides?.ogImageUrl || "";

  const activeTitle = customTitle.trim() || autoTitle;
  const activeDescription = customDescription.trim() || autoDescription;
  const activeImage = customImageUrl.trim() || profile.photoUrl || "";

  const titleLength = activeTitle.length;
  const descLength = activeDescription.length;

  const handleTitleChange = (newTitle: string) => {
    if (!isProUser) {
      onUpgradeClick?.();
      return;
    }
    onUpdateSeo?.({
      ...profile.seoOverrides,
      ogTitle: newTitle,
    });
  };

  const handleDescriptionChange = (newDesc: string) => {
    if (!isProUser) {
      onUpgradeClick?.();
      return;
    }
    onUpdateSeo?.({
      ...profile.seoOverrides,
      ogDescription: newDesc,
    });
  };

  const handleImageChange = (newImageUrl: string) => {
    if (!isProUser) {
      onUpgradeClick?.();
      return;
    }
    onUpdateSeo?.({
      ...profile.seoOverrides,
      ogImageUrl: newImageUrl,
    });
  };

  const handleResetSeo = () => {
    if (!isProUser) return;
    onUpdateSeo?.({});
  };

  const hasOverrides = Boolean(customTitle || customDescription || customImageUrl);

  return (
    <div className="w-full max-w-2xl mx-auto space-y-8 p-6 animate-in fade-in duration-200">
      {/* ─── SEO Metadata Editor Controls ────────────────────────────── */}
      <div className="bg-card rounded-xl p-5 border border-border shadow-xs space-y-5">
        <div className="flex items-center justify-between border-b border-border/80 pb-3">
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-amber-500/10 text-amber-600">
              <Sparkles className="h-4 w-4" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-foreground">Search Engine & Social Metadata</h3>
                <ProBadge locked={!isProUser} />
              </div>
              <p className="text-[11px] text-muted-foreground">
                Customize how your academic website appears on Google, Twitter, LinkedIn, and WhatsApp.
              </p>
            </div>
          </div>

          {hasOverrides && onUpdateSeo && isProUser && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleResetSeo}
              className="text-xs h-7 gap-1 text-muted-foreground hover:text-foreground"
              title="Reset to default auto-generated metadata"
            >
              <RotateCcw className="h-3 w-3" />
              <span>Reset</span>
            </Button>
          )}
        </div>

        {!isProUser && (
          <div className="p-3 bg-amber-50/70 border border-amber-200/80 rounded-lg flex items-center justify-between gap-3 text-xs">
            <div className="flex items-center gap-2 text-amber-900">
              <Lock className="h-4 w-4 text-amber-700 shrink-0" />
              <span>
                <strong>Pro Feature:</strong> Custom search snippets and social cards are available on Pro plans. Below is your live auto-generated preview.
              </span>
            </div>
            <Button
              size="sm"
              onClick={onUpgradeClick}
              className="h-7 px-3 text-xs bg-amber-600 hover:bg-amber-700 text-white font-medium shrink-0 shadow-2xs"
            >
              Upgrade
            </Button>
          </div>
        )}

        {onUpdateSeo ? (
          <div className={`space-y-4 ${!isProUser ? "opacity-60 pointer-events-none" : ""}`}>
            {/* Custom SEO Title */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label htmlFor="seo-title-input" className="text-xs font-semibold text-foreground">
                  Custom Page & Social Title (OG:Title)
                </label>
                <span className={`text-[11px] ${titleLength > 60 ? "text-amber-600 font-medium" : "text-muted-foreground"}`}>
                  {titleLength}/60 chars (Optimal)
                </span>
              </div>
              <Input
                id="seo-title-input"
                disabled={!isProUser}
                placeholder={autoTitle}
                value={customTitle}
                onChange={(e) => handleTitleChange(e.target.value)}
                className="text-xs h-9"
              />
              <p className="text-[11px] text-muted-foreground">
                Leave blank to use the auto-generated academic title.
              </p>
            </div>

            {/* Custom SEO Description */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label htmlFor="seo-desc-input" className="text-xs font-semibold text-foreground">
                  Custom Meta Description (OG:Description)
                </label>
                <span className={`text-[11px] ${descLength > 160 ? "text-amber-600 font-medium" : "text-muted-foreground"}`}>
                  {descLength}/160 chars (Optimal)
                </span>
              </div>
              <Textarea
                id="seo-desc-input"
                disabled={!isProUser}
                placeholder={autoDescription}
                value={customDescription}
                onChange={(e) => handleDescriptionChange(e.target.value)}
                className="text-xs h-20 resize-none leading-relaxed"
              />
              <p className="text-[11px] text-muted-foreground">
                A concise summary of your research focus and academic affiliations.
              </p>
            </div>

            {/* Custom Social Share Image */}
            <div className="space-y-1.5 pt-1">
              <label className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                <ImageIcon className="h-3.5 w-3.5 text-muted-foreground" />
                Custom Social Share Image (OG:Image)
              </label>
              <div className="flex items-center gap-3">
                <ImageUpload
                  value={customImageUrl || null}
                  onChange={handleImageChange}
                  category="figure"
                  maxSizeMB={5}
                />
                {customImageUrl && isProUser && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleImageChange("")}
                    className="text-xs h-8 text-destructive border-destructive/30 hover:bg-destructive/10"
                  >
                    Clear Custom Image
                  </Button>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground">
                Recommended 1200x630px. If not set, your profile photo is used automatically.
              </p>
            </div>
          </div>
        ) : null}
      </div>

      {/* ─── LIVE GOOGLE SEARCH RESULT CARD ───────────────────────────── */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Globe className="h-4 w-4 text-blue-600" />
            <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Google Search Result Snippet
            </h3>
          </div>
          <span className="text-[10px] text-muted-foreground">Live Simulation</span>
        </div>

        <div className="bg-white p-4 rounded-xl border border-border/80 shadow-xs space-y-1">
          <div className="flex items-center gap-2 text-xs text-zinc-600 truncate mb-0.5">
            <span className="font-medium text-zinc-900">profolio.lk</span>
            <span>› {subdomain}</span>
          </div>

          <h4 className="text-base text-[#1a0dab] hover:underline font-medium cursor-pointer leading-snug line-clamp-1">
            {activeTitle}
          </h4>

          <p className="text-xs text-[#4d5156] leading-relaxed line-clamp-2">
            {activeDescription}
          </p>
        </div>
      </div>

      {/* ─── LIVE SOCIAL MEDIA SHARE CARD ─────────────────────────────── */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Share2 className="h-4 w-4 text-indigo-600" />
            <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Twitter / LinkedIn / WhatsApp Preview Card
            </h3>
          </div>
          <span className="text-[10px] text-muted-foreground">Open Graph Card</span>
        </div>

        <div className="bg-card rounded-xl border border-border/80 overflow-hidden shadow-xs">
          {/* Card Media Preview */}
          <div className="h-48 w-full bg-muted/60 relative flex items-center justify-center overflow-hidden border-b border-border/60">
            {activeImage ? (
              <img
                src={activeImage}
                alt="Social Card Thumbnail"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="flex flex-col items-center gap-2 text-muted-foreground/60">
                <ImageIcon className="h-10 w-10 stroke-1" />
                <span className="text-xs font-medium">Auto-generated profile banner</span>
              </div>
            )}
            <div className="absolute top-2 left-2 bg-black/60 backdrop-blur-xs text-white text-[10px] font-mono px-2 py-0.5 rounded-md">
              1200 × 630
            </div>
          </div>

          {/* Card Body */}
          <div className="p-3.5 space-y-1 bg-white">
            <div className="text-[11px] text-muted-foreground uppercase font-mono tracking-wider">
              {subdomain}.profolio.lk
            </div>
            <div className="text-sm font-bold text-foreground leading-snug line-clamp-1">
              {activeTitle}
            </div>
            <div className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">
              {activeDescription}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
